import { useRef, useState } from 'react';
import { Pencil } from '../../icons';
import type { CustomFieldDto } from '../../api/task-extras';
import { tTask } from '../../lib/task-i18n';

interface FieldNameEditorProps {
  field: Pick<CustomFieldDto, 'id' | 'name'>;
  onRename: (fieldId: string, name: string) => Promise<void>;
}

/**
 * 커스텀 필드 설정의 이름 칸 — 눌러서 그 자리에서 고친다 (2026-09-11, QA AXI-TSK-02-TC006).
 *
 * 종전 설정 목록에는 삭제만 있었다. 필드를 만들 때 이름 칸에 종류 이름('텍스트' 등)이 미리
 * 채워지는데, 그대로 만들면 같은 이름의 칸이 여러 개 생기고 되돌릴 길이 없었다.
 * 서버 PATCH /custom-fields/:id 는 원래 name 을 받는다 — 화면만 없었다.
 *
 * Enter·포커스 이탈 = 저장, Esc = 취소. 빈 이름·같은 이름은 보내지 않는다(서버도 빈 이름은 400).
 */
export function FieldNameEditor({ field, onRename }: FieldNameEditorProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(field.name);
  const [saving, setSaving] = useState(false);
  // Enter 로 저장한 뒤 이어지는 blur 가 한 번 더 저장하지 않게 — 한 번의 편집에 한 번만 끝낸다.
  const doneRef = useRef(false);
  const label = tTask('task.field.rename_aria', { name: field.name });

  function start() {
    setDraft(field.name);
    doneRef.current = false;
    setEditing(true);
  }

  async function commit() {
    if (doneRef.current) return;
    doneRef.current = true;
    const next = draft.trim();
    if (!next || next === field.name) { setEditing(false); return; }
    setSaving(true);
    try {
      await onRename(field.id, next);
    } catch {
      /* 실패하면 원래 이름이 그대로 보인다 — 목록은 부모가 다시 읽을 때 서버 값으로 맞춰진다. */
    } finally {
      setSaving(false);
      setEditing(false);
    }
  }

  if (!editing) {
    return (
      <button
        type="button"
        className="tt-fieldmanage-name tt-fieldmanage-rename"
        onClick={start}
        aria-label={label}
        data-tip={label}
      >
        <span className="tt-fieldmanage-rename-text">{field.name}</span>
        <Pencil size={11} aria-hidden />
      </button>
    );
  }

  return (
    <input
      className="tt-fieldmanage-name tt-fieldmanage-input"
      value={draft}
      maxLength={60}
      autoFocus
      disabled={saving}
      aria-label={tTask('task.field.name_label')}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => void commit()}
      onKeyDown={(e) => {
        if (e.key === 'Enter') { e.preventDefault(); void commit(); }
        // Esc 는 이름 편집만 취소한다 — 메뉴 전체를 닫는 문서 단위 Esc 처리까지 가지 않게 막는다.
        if (e.key === 'Escape') { e.stopPropagation(); doneRef.current = true; setEditing(false); }
      }}
    />
  );
}
